import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Table, Button, Alert, Badge, Form } from 'react-bootstrap';
import {
  FiActivity,
  FiClock,
  FiCreditCard,
  FiLayers,
} from 'react-icons/fi';
import UserLayout from '../../components/user/UserLayout';
import PaginationControls from '../../components/common/PaginationControls';
import api from '../../lib/api';
import { useToast } from '../../components/common/ToastProvider';

export default function UserOverviewPage() {
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState({ results: [], previous: null, next: null, page: 1 });
  const [msg, setMsg] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [provider, setProvider] = useState('stripe');
  const [ngnRate, setNgnRate] = useState(null);
  const { showToast } = useToast();
  const getError = (e, fallback) =>
    e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback;

  const load = async (page = 1) => {
    try {
      const [sub, sum, hist] = await Promise.all([
        api.get('/user/settings/subscription/'),
        api.get('/user/settings/summary/'),
        api.get('/user/settings/payment-history/', { params: { page, page_size: 8 } }),
      ]);

      setSubscription(sub.data.subscription || null);
      setPlans(sub.data.plans || []);
      setSummary(sum.data.workspace_summary || null);
      setHistory({ ...(hist.data || {}), page });
    } catch (error) {
      const errorMessage = getError(error, 'Unable to load dashboard data.');
      setMsg(errorMessage);
      showToast(errorMessage, 'danger');
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (provider !== 'paystack') return;
    fetch('https://open.er-api.com/v6/latest/USD')
      .then((r) => r.json())
      .then((data) => setNgnRate(Number(data?.rates?.NGN) || null))
      .catch(() => setNgnRate(null));
  }, [provider]);

  const startCheckout = async (planId, provider, billing_cycle) => {
    try {
      const res = await api.post('/user/settings/checkout/', {
        plan_id: planId,
        provider,
        billing_cycle,
      });

      const url = res?.data?.checkout?.url || res?.data?.checkout?.authorization_url;

      if (url) {
        window.location.href = url;
      } else {
        setMsg('Checkout initialized but no redirect URL returned.');
        showToast('Checkout initialized but no redirect URL returned.', 'warning');
      }
    } catch (e) {
      const errorMessage = getError(e, 'Failed to start checkout');
      setMsg(errorMessage);
      showToast(errorMessage, 'danger');
    }
  };

  const stats = [
    {
      label: 'Current Plan',
      value: subscription?.plan?.name || 'N/A',
      icon: <FiCreditCard />,
    },
    {
      label: 'Status',
      value: subscription?.status || 'N/A',
      icon: <FiActivity />,
    },
    {
      label: 'Billing Cycle',
      value: subscription?.billing_cycle || 'N/A',
      icon: <FiLayers />,
    },
    {
      label: 'Ends On',
      value: subscription?.current_period_end
        ? new Date(subscription.current_period_end).toLocaleDateString()
        : 'N/A',
      icon: <FiClock />,
    },
  ];

  return (
    <UserLayout title="Subscription & Billing" subtitle="Manage your plan, workspace usage, and billing history">
      {msg && <Alert variant="info">{msg}</Alert>}

      <Row className="g-4 mb-4">
        {stats.map((item) => (
          <Col xl={3} md={6} key={item.label}>
            <Card className="user-stat-card p-3">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <div className="user-stat-label">{item.label}</div>
                  <div className="user-stat-value" style={{ fontSize: '1.2rem' }}>
                    {item.value}
                  </div>
                </div>
                <div className="user-stat-icon">{item.icon}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {summary && (
        <Card className="user-card p-4 mb-4">
          <div className="user-section-title">Workspace Summary</div>
          <Row className="g-3">
            <Col md={3}>
              <div className="p-3 rounded-4 bg-light h-100">
                <div className="text-muted small">Widgets</div>
                <div className="fw-bold fs-4">{summary.widgets}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="p-3 rounded-4 bg-light h-100">
                <div className="text-muted small">Training Sources</div>
                <div className="fw-bold fs-4">{summary.training_sources}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="p-3 rounded-4 bg-light h-100">
                <div className="text-muted small">Chat Messages</div>
                <div className="fw-bold fs-4">{summary.chat_messages}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="p-3 rounded-4 bg-light h-100">
                <div className="text-muted small">Payment Events</div>
                <div className="fw-bold fs-4">{summary.events}</div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <Card className="user-card p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="user-section-title mb-0">Available Plans</div>
          <Badge bg="primary" pill>
            {plans.length} Plans
          </Badge>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-4">
          <Form.Select
            style={{ maxWidth: 220 }}
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          >
            <option value="stripe">Pay with Stripe (USD)</option>
            <option value="paystack">Pay with Paystack (NGN)</option>
          </Form.Select>
          <Form.Select
            style={{ maxWidth: 220 }}
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value)}
          >
            <option value="monthly">Monthly Billing</option>
            <option value="annual">Yearly Billing</option>
          </Form.Select>
        </div>

        <Row className="g-3">
          {plans.map((p) => {
            const usd = Number(billingCycle === 'annual' ? p.annual_price_usd : p.monthly_price_usd) || 0;
            const displayPrice = provider === 'paystack' && ngnRate
              ? `₦${Math.round(usd * ngnRate).toLocaleString()}`
              : `$${usd.toLocaleString()}`;
            let meta = p.metadata || {};
            if (typeof p.metadata === 'string') {
              try {
                meta = JSON.parse(p.metadata || '{}');
              } catch {
                meta = {};
              }
            }
            const enabledFeatures = Object.entries(meta?.features || {}).filter(([, enabled]) => !!enabled).map(([code]) => code.replace('feature_', '').replaceAll('_', ' '));
            const limits = Object.entries(meta?.limits || {}).filter(([, value]) => value !== null && value !== undefined);
            return (
              <Col lg={4} md={6} key={p.id} className="d-flex">
                <Card className="user-card p-4 w-100 d-flex flex-column">
                  <div className="fw-bold fs-5">{p.name}</div>
                  <div className="text-muted small mb-2">{billingCycle === 'annual' ? 'Yearly' : 'Monthly'} plan</div>
                  <div className="display-6 fw-bold mb-3">{displayPrice}</div>
                  <div className="text-muted small mb-4">
                    {provider === 'paystack' && !ngnRate ? 'Live NGN rate unavailable, showing USD fallback.' : 'Tax may apply based on region.'}
                  </div>
                  <div className="small mb-3">
                    <div className="fw-semibold mb-1">Plan includes</div>
                    <ul className="mb-0 ps-3">
                      {(enabledFeatures.length ? enabledFeatures.slice(0, 4) : ['core access']).map((feature) => (
                        <li key={feature} className="text-capitalize">{feature}</li>
                      ))}
                      {limits.length ? <li>{limits.length} usage limits configured</li> : null}
                    </ul>
                  </div>
                  <div className="mt-auto">
                    <Button
                      className="w-100 user-quick-action"
                      variant={provider === 'paystack' ? 'success' : 'primary'}
                      onClick={() => startCheckout(p.id, provider, billingCycle)}
                    >
                      {provider === 'paystack' ? 'Pay with Paystack' : 'Pay with Stripe'}
                    </Button>
                  </div>
                </Card>
              </Col>
            );
          })}
          {!plans.length && (
            <Col>
              <div className="text-center text-muted py-4">No plans available.</div>
            </Col>
          )}
        </Row>
      </Card>

      <Card className="user-card p-4">
        <div className="user-section-title">Payment History</div>

        <div className="table-responsive user-table-wrap">
          <Table hover>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Event</th>
                <th>Processed</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {(history.results || []).map((e) => (
                <tr key={`${e.provider}-${e.event_id}`}>
                  <td className="text-capitalize">{e.provider}</td>
                  <td>{e.event_type}</td>
                  <td>
                    <Badge bg={e.processed ? 'success' : 'secondary'}>
                      {e.processed ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  <td>{new Date(e.created_at).toLocaleString()}</td>
                </tr>
              ))}

              {!history.results?.length && (
                <tr>
                  <td colSpan="4" className="text-center text-muted py-4">
                    No payment history found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <PaginationControls
          page={history.page}
          hasPrev={!!history.previous}
          hasNext={!!history.next}
          onPrev={() => history.previous && load(Math.max(1, history.page - 1))}
          onNext={() => history.next && load(history.page + 1)}
        />
      </Card>
    </UserLayout>
  );
}
